import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import {
    PawPrint, SignOut, UserCircle, List, X, CaretLeft, CaretRight, Star,
    Question, House, Siren, Users, ChatCircle, ShieldCheck, Heart
} from "@phosphor-icons/react";
import { PawLinkLogo } from "@/components/PawLinkLogo";
// Note: previous "Support"/"Help" nav items used a mailto: anchor which opens
// the OS email chooser and breaks the in-app experience. All support entry
// points now route to the internal `/support` page. The `supportMailto` helper
// and `DrawerAnchor` component are intentionally no longer used in Nav.
import api from "@/lib/api";

/**
 * Nav — global, single navigation for both website and app.
 *
 * Design goals (Apple / Stripe / Linear feel):
 *  • Header contains ONLY logo + hamburger + back button on non-home pages.
 *  • ONE side drawer for every viewport (desktop, tablet, mobile).
 *  • Guest vs authenticated items diverge inside the drawer, never in the header.
 */
export default function Nav() {
    const { user, logout } = useAuth();
    const { t } = useI18n();
    const navigate = useNavigate();
    const location = useLocation();

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    // Mini-dashboard stats shown at the top of the drawer: total pets + how many
    // are currently in Lost Mode.  Refreshed every time the drawer opens so the
    // user never sees a stale count.  Real data only — no fabricated defaults.
    const [petStats, setPetStats] = useState({ total: 0, lost: 0, loaded: false });

    const loadNotifs = async () => {
        if (!user) return;
        try {
            const { data } = await api.get("/notifications");
            setUnreadCount(data?.unread || 0);
        } catch (_) { /* ignore */ }
    };

    const loadPetStats = async () => {
        if (!user) return;
        try {
            const { data } = await api.get("/pets");
            const list = Array.isArray(data) ? data : (data.items || []);
            const total = list.length;
            const lost = list.filter((p) => p.is_lost).length;
            setPetStats({ total, lost, loaded: true });
        } catch (_) { setPetStats({ total: 0, lost: 0, loaded: true }); }
    };

    useEffect(() => {
        if (!user) { setUnreadCount(0); return; }
        loadNotifs();
        const iv = setInterval(loadNotifs, 20000);
        return () => clearInterval(iv);
    }, [user?.id]);

    // Close drawer on route change
    useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

    // Refresh mini-dashboard when the drawer opens (cheap: one /pets call).
    useEffect(() => { if (drawerOpen && user) loadPetStats(); }, [drawerOpen, user?.id]);

    // Lock body scroll while drawer is open
    useEffect(() => {
        if (drawerOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => { document.body.style.overflow = ""; };
    }, [drawerOpen]);

    // Determine if back button should be shown
    const path = location.pathname;
    const noBackRoutes = ["/", "/home", "/login", "/register"];
    const showBack = !noBackRoutes.includes(path);

    const isPremium = user?.plan === "premium";
    const isTrialing =
        user?.subscription_status === "trialing" ||
        (user?.trial_ends_at && new Date(user.trial_ends_at) > new Date());
    const hasUnread = unreadCount > 0;

    const goBack = () => {
        if (window.history.length > 1) navigate(-1);
        else navigate(user ? "/home" : "/");
    };

    return (
        <>
            <header
                data-testid="site-nav"
                className="fixed top-0 inset-x-0 z-40 w-full bg-paw-night/95 backdrop-blur text-white border-b border-white/[0.06]"
            >
                {/* Subtle warm hairline at the bottom for brand feel */}
                <div aria-hidden className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-paw-terra/40 to-transparent" />

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-2">
                    {/* Back button (all non-home routes) */}
                    {showBack && (
                        <button
                            type="button"
                            onClick={goBack}
                            data-testid="nav-back-btn"
                            aria-label={t("nav.back")}
                            className="inline-flex items-center gap-1.5 rounded-full text-white/85 hover:text-white hover:bg-white/[0.08] pl-2 pr-3 py-2 transition"
                        >
                            <CaretLeft size={18} weight="bold" className="rtl:hidden" />
                            <CaretRight size={18} weight="bold" className="hidden rtl:inline" />
                            <span className="text-sm font-medium hidden sm:inline">{t("nav.back")}</span>
                        </button>
                    )}

                    {/* Logo — always centered relative to nav row */}
                    <Link
                        to={user ? "/home" : "/"}
                        data-testid="nav-logo"
                        className={`flex items-center gap-2.5 group ${showBack ? "mx-auto" : "me-auto"}`}
                    >
                        <PawLinkLogo size={34} data-testid="nav-brand-mark" />
                        <span className="font-display text-xl font-medium tracking-tight leading-none notranslate" dir="ltr" translate="no">
                            <span className="text-white">Paw</span><span className="bg-gradient-to-r from-[#F87A2A] to-[#FF8A00] bg-clip-text text-transparent">Link</span>
                        </span>
                    </Link>

                    {/* Hamburger — universal, all viewports */}
                    <button
                        type="button"
                        data-testid="nav-menu-btn"
                        onClick={() => setDrawerOpen(true)}
                        aria-label={t("nav.menu")}
                        className="relative inline-flex items-center justify-center w-11 h-11 rounded-full text-white/90 hover:text-white hover:bg-white/[0.08] transition"
                    >
                        <List size={22} weight="bold" />
                        {user && hasUnread && (
                            <span
                                data-testid="nav-menu-unread-dot"
                                aria-hidden
                                className="absolute top-2 end-2 w-2.5 h-2.5 rounded-full bg-paw-terra ring-2 ring-paw-night"
                            />
                        )}
                    </button>
                </div>
            </header>

            {/* Drawer */}
            {drawerOpen && (
                <>
                    <div
                        data-testid="nav-drawer-backdrop"
                        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm transition-opacity"
                        onClick={() => setDrawerOpen(false)}
                    />
                    <aside
                        data-testid="nav-drawer"
                        className="fixed top-0 end-0 z-50 h-full w-full sm:w-[420px] bg-white dark:bg-[hsl(152,15%,8%)] shadow-2xl flex flex-col overflow-hidden"
                    >
                        {/* Drawer header — mini dashboard for signed-in users */}
                        <div className="relative px-5 py-5 bg-gradient-to-br from-paw-forest to-paw-forestDark dark:from-[hsl(152,15%,10%)] dark:to-[hsl(152,15%,6%)] text-white overflow-hidden">
                            <div aria-hidden className="absolute -top-10 -end-10 w-40 h-40 rounded-full bg-paw-terra/25 blur-2xl pointer-events-none" />

                            {user ? (
                                <>
                                    <div className="flex items-start gap-3">
                                        <Avatar user={user} size={52} />
                                        <div className="min-w-0 flex-1 relative">
                                            <p className="font-display text-xl text-white truncate leading-tight">{user.name || user.email}</p>
                                            <div
                                                data-testid="nav-drawer-plan-badge"
                                                className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur px-2.5 py-0.5 text-[10px] uppercase tracking-wider font-semibold"
                                            >
                                                {isPremium ? (
                                                    <><Star size={10} weight="fill" className="text-paw-terra" /> {t("nav.plan_premium")}</>
                                                ) : isTrialing ? (
                                                    <><Star size={10} weight="duotone" className="text-amber-300" /> {t("nav.plan_trial")}</>
                                                ) : (t("nav.plan_free"))}
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setDrawerOpen(false)}
                                            data-testid="nav-drawer-close"
                                            aria-label="Close"
                                            className="relative inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white transition"
                                        >
                                            <X size={18} weight="bold" />
                                        </button>
                                    </div>

                                    {/* Mini dashboard — pets count + safety status */}
                                    <div
                                        data-testid="nav-drawer-mini-dashboard"
                                        className="relative mt-4 grid grid-cols-2 gap-2"
                                    >
                                        {petStats.loaded && petStats.total === 0 ? (
                                            <div className="col-span-2 rounded-2xl bg-white/10 backdrop-blur border border-white/10 px-3 py-2.5 text-center">
                                                <p className="text-[13px] text-white/90 font-medium">{t("nav.no_pets_yet")}</p>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="rounded-2xl bg-white/10 backdrop-blur border border-white/10 px-3 py-2.5 flex items-center gap-2">
                                                    <PawPrint size={16} weight="fill" className="text-paw-terra flex-shrink-0" />
                                                    <div className="min-w-0">
                                                        <p className="text-[10px] uppercase tracking-wider text-white/60 leading-none">{t("nav.dash_pets")}</p>
                                                        <p className="mt-0.5 text-sm font-display leading-none tabular-nums" data-testid="nav-dash-pets-count">{petStats.loaded ? petStats.total : "—"}</p>
                                                    </div>
                                                </div>
                                                <div
                                                    data-testid="nav-dash-status"
                                                    className={`rounded-2xl backdrop-blur border px-3 py-2.5 flex items-center gap-2 ${
                                                        petStats.lost > 0
                                                            ? "bg-red-500/25 border-red-400/30"
                                                            : "bg-emerald-500/20 border-emerald-400/25"
                                                    }`}
                                                >
                                                    <Heart size={16} weight="fill" className={petStats.lost > 0 ? "text-red-200 flex-shrink-0" : "text-emerald-200 flex-shrink-0"} />
                                                    <div className="min-w-0">
                                                        <p className="text-[10px] uppercase tracking-wider text-white/60 leading-none">{t("nav.dash_status")}</p>
                                                        <p className="mt-0.5 text-[12px] font-semibold leading-tight truncate">
                                                            {petStats.lost > 0
                                                                ? t("nav.dash_status_lost", { n: petStats.lost })
                                                                : t("nav.dash_status_safe")}
                                                        </p>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <div className="flex items-center gap-3">
                                    <PawLinkLogo size={44} />
                                    <div className="min-w-0 flex-1 relative">
                                        <p className="font-display text-xl text-white leading-none notranslate" dir="ltr" translate="no">
                                            <span>Paw</span><span className="text-paw-terra">Link</span>
                                        </p>
                                        <p className="mt-1.5 text-[11px] text-white/75 uppercase tracking-[0.18em]">
                                            {t("home_story.hero_eyebrow")}
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setDrawerOpen(false)}
                                        data-testid="nav-drawer-close"
                                        aria-label="Close"
                                        className="relative inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white transition"
                                    >
                                        <X size={18} weight="bold" />
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Menu body */}
                        <nav className="flex-1 overflow-y-auto py-2">
                            {user ? (
                                <>
                                    <DrawerItem to="/home" icon={<House size={18} weight="duotone" />} label={t("nav.home")} testId="drawer-home" />
                                    <DrawerItem to="/pets" icon={<PawPrint size={18} weight="duotone" />} label={t("nav.my_pets")} testId="drawer-my-pets" />
                                    <DrawerItem
                                        to="/rescue"
                                        icon={<Siren size={18} weight="duotone" />}
                                        label={t("nav.rescue_center")}
                                        testId="drawer-rescue"
                                        trailing={hasUnread ? <span className="rounded-full bg-paw-terra text-white text-[10px] font-bold px-2 py-0.5 leading-none">{unreadCount > 9 ? "9+" : unreadCount}</span> : null}
                                    />
                                    <DrawerItem to="/community" icon={<Users size={18} weight="duotone" />} label={t("nav.community")} testId="drawer-community" />
                                    <DrawerItem
                                        to="/subscription"
                                        icon={<ShieldCheck size={18} weight="duotone" />}
                                        label={t("nav.safety")}
                                        testId="drawer-safety"
                                        trailing={isPremium ? <span className="rounded-full bg-paw-terra/15 text-paw-terra text-[10px] font-semibold px-2 py-0.5 leading-none border border-paw-terra/25">Premium</span> : null}
                                    />
                                    <Divider />
                                    <DrawerItem to="/account" icon={<UserCircle size={18} weight="duotone" />} label={t("nav.account")} testId="drawer-account" />
                                    <DrawerItem
                                        to="/support"
                                        icon={<Question size={18} weight="duotone" />}
                                        label={t("nav.help")}
                                        testId="drawer-help"
                                    />
                                </>
                            ) : (
                                <>
                                    <DrawerItem to="/" icon={<House size={18} weight="duotone" />} label={t("nav.home")} testId="drawer-home" />
                                    <DrawerItem to="/subscription" icon={<ShieldCheck size={18} weight="duotone" />} label={t("nav.safety")} testId="drawer-safety" />
                                    <DrawerItem to="/community" icon={<Users size={18} weight="duotone" />} label={t("nav.community")} testId="drawer-community" />
                                    <DrawerItem to="/faq" icon={<ChatCircle size={18} weight="duotone" />} label={t("nav.faq")} testId="drawer-faq" />
                                    <DrawerItem
                                        to="/support"
                                        icon={<Question size={18} weight="duotone" />}
                                        label={t("nav.support")}
                                        testId="drawer-support"
                                    />
                                </>
                            )}
                        </nav>

                        {/* Drawer footer — auth actions */}
                        <div className="border-t border-paw-line dark:border-white/10 p-3 space-y-2">
                            {user ? (
                                <button
                                    data-testid="drawer-signout"
                                    onClick={async () => { setDrawerOpen(false); await logout(); navigate("/"); }}
                                    className="w-full flex items-center justify-center gap-2 rounded-full py-3 text-sm font-semibold text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 transition"
                                >
                                    <SignOut size={16} /> <span>{t("nav.signout")}</span>
                                </button>
                            ) : (
                                <>
                                    <Link
                                        to="/login"
                                        onClick={() => setDrawerOpen(false)}
                                        data-testid="drawer-login"
                                        className="w-full flex items-center justify-center gap-2 rounded-full border border-paw-line dark:border-white/15 py-3 text-sm font-semibold text-paw-ink dark:text-paw-bone hover:bg-paw-muted dark:hover:bg-white/5 transition"
                                    >
                                        {t("nav.login")}
                                    </Link>
                                    <Link
                                        to="/register"
                                        onClick={() => setDrawerOpen(false)}
                                        data-testid="drawer-register"
                                        className="w-full flex items-center justify-center gap-2 rounded-full bg-paw-terra hover:bg-paw-terraDark py-3 text-sm font-semibold text-white transition"
                                    >
                                        {t("nav.create_account")}
                                    </Link>
                                </>
                            )}
                        </div>
                    </aside>
                </>
            )}
        </>
    );
}

/* ---------------- Sub-components ---------------- */
function Avatar({ user, size = 32 }) {
    const initial = (user?.name || user?.email || "?").trim().charAt(0).toUpperCase();
    if (user?.picture) {
        return (
            <img
                src={user.picture}
                alt=""
                className="rounded-full object-cover flex-shrink-0 relative"
                style={{ width: size, height: size }}
            />
        );
    }
    return (
        <span
            className="grid place-items-center rounded-full bg-paw-terra text-white font-semibold flex-shrink-0 relative"
            style={{ width: size, height: size, fontSize: size * 0.42 }}
            aria-hidden
        >
            {initial}
        </span>
    );
}

function DrawerItem({ to, icon, label, testId, trailing }) {
    return (
        <Link
            to={to}
            data-testid={testId}
            className="mx-2 flex items-center gap-3 rounded-xl px-3 py-2.5 text-[14px] font-medium text-paw-ink dark:text-paw-bone hover:bg-paw-muted dark:hover:bg-white/5 transition"
        >
            <span className="w-8 h-8 rounded-lg bg-paw-forest/8 dark:bg-paw-terra/12 text-paw-forest dark:text-paw-terra grid place-items-center flex-shrink-0">
                {icon}
            </span>
            <span className="flex-1 truncate leading-none">{label}</span>
            {trailing}
        </Link>
    );
}

function DrawerAnchor({ href, icon, label, testId, trailing }) {
    return (
        <a
            href={href}
            data-testid={testId}
            className="mx-2 flex items-center gap-3 rounded-xl px-3 py-2.5 text-[14px] font-medium text-paw-ink dark:text-paw-bone hover:bg-paw-muted dark:hover:bg-white/5 transition"
        >
            <span className="w-8 h-8 rounded-lg bg-paw-forest/8 dark:bg-paw-terra/12 text-paw-forest dark:text-paw-terra grid place-items-center flex-shrink-0">
                {icon}
            </span>
            <span className="flex-1 truncate leading-none">{label}</span>
            {trailing}
        </a>
    );
}

function Divider() {
    return <div className="my-1.5 mx-6 border-t border-paw-line/70 dark:border-white/10" />;
}
